// apps/client/src/hooks/useTrailDropOff.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface UseTrailDropOffParams {
  companyId: string;
  startMonth: string; // YYYY-MM format
  endMonth: string; // YYYY-MM format
  brokerId?: string;
  officeId?: string;
  templateId?: string;
  alertType?: 'all' | 'current_month_dropoff' | 'previous_months_dropoff' | 'historical_dropoff' | 'recovered_dropoff' | 'significant_variance' | 'second_year_commencement';
  /**
   * Slide the alert window back to the tenant's newest completed upload month,
   * keeping the requested span. For callers whose window comes from the wall
   * clock (the Who To Call strip). No-op when the tenant's data is current.
   */
  anchorWindowToLatestData?: boolean;
  entityCode?: string;
}

interface TrailDropOffRecord {
  loanId: string;
  brokerId: string;
  brokerDisplay: string;
  customerName: string | null;
  lender: string | null;
  settlementDate: Date;
  /**
   * 'YYYY-MM-DD' when the loan has a discharge row, else null. Deliberately a
   * STRING, not a Date: it is displayed verbatim on the discharge chip, and
   * `new Date('YYYY-MM-DD')` would reinterpret it in UTC and can render the
   * previous day in Australian time zones.
   */
  dischargeDate: string | null;
  paymentMonth: string;
  trailCents: number;
  priorMonthCents: number | null;
  priorPaymentMonth: string | null;
  monthsSinceSettlement: number;
  alertType: 'current_month_dropoff' | 'previous_months_dropoff' | 'historical_dropoff' | 'recovered_dropoff' | 'significant_variance' | 'second_year_commencement' | null;
  variancePercent: number | null;
  officeId: string | null;
  templateId: string | null;
}

interface MonthlyTrend {
  month: string;
  currentMonthDropOffs: number;
  previousMonthsDropOffs: number;
  significantVariances: number;
  secondYearCommencements: number;
  /** Optional: absent while an older backend is still deployed. */
  historicalDropOffs?: number;
  recoveredDropOffs?: number;
}

export interface DataWindow {
  templateId: string;
  comparisonMonth: string | null;
  knowledgeCutoffMonth: string | null;
}

export interface RunCompletenessInfo {
  templateId: string;
  runStrategy: 'sequential' | 'single';
  availableRuns: number[];
  expectedRunCount: number;
  isComplete: boolean;
}

interface UseTrailDropOffReturn {
  records: TrailDropOffRecord[];
  monthlyTrend: MonthlyTrend[];
  loading: boolean;
  error: string | null;
  totalAlerts: number;
  currentMonthDropOffs: number;
  previousMonthsDropOffs: number;
  significantVariances: number;
  secondYearCommencements: number;
  historicalDropOffs: number;
  recoveredDropOffs: number;
  /** current + multi-month gap + earlier unresolved + variance. Excludes resumed and 2nd-year. */
  openAlertCount: number;
  /** Rows actually returned. The table is LIMIT 2000 while the cards are unbounded. */
  returnedRecordCount: number;
  recordsTruncated: boolean;
  /** Per-template data currency, so months can be labelled honestly. */
  dataWindows: DataWindow[];
  /** Run completeness metadata for the end month */
  runCompleteness: RunCompletenessInfo[];
}

/**
 * Hook to fetch trail drop-off report from BigQuery
 *
 * Queries raw_commissions table to analyze trail commission patterns.
 * Returns individual alert records with monthly trends.
 *
 * RBAC: Automatically filters to broker's data if brokerId is provided
 */
export function useTrailDropOff({
  companyId,
  startMonth,
  endMonth,
  brokerId,
  officeId,
  templateId,
  alertType = 'all',
  anchorWindowToLatestData = false,
  entityCode,
}: UseTrailDropOffParams): UseTrailDropOffReturn {
  const [records, setRecords] = useState<TrailDropOffRecord[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [currentMonthDropOffs, setCurrentMonthDropOffs] = useState(0);
  const [previousMonthsDropOffs, setPreviousMonthsDropOffs] = useState(0);
  const [significantVariances, setSignificantVariances] = useState(0);
  const [secondYearCommencements, setSecondYearCommencements] = useState(0);
  const [historicalDropOffs, setHistoricalDropOffs] = useState(0);
  const [recoveredDropOffs, setRecoveredDropOffs] = useState(0);
  const [openAlertCount, setOpenAlertCount] = useState(0);
  const [returnedRecordCount, setReturnedRecordCount] = useState(0);
  const [recordsTruncated, setRecordsTruncated] = useState(false);
  const [dataWindows, setDataWindows] = useState<DataWindow[]>([]);
  const [runCompleteness, setRunCompleteness] = useState<RunCompletenessInfo[]>([]);

  useEffect(() => {
    if (!companyId || !startMonth || !endMonth) {
      setLoading(false);
      return;
    }

    const fetchTrailDropOff = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`[useTrailDropOff] Calling BigQuery getTrailDropOffReport:`, {
          companyId,
          startMonth,
          endMonth,
          brokerId: brokerId || 'all',
          officeId: officeId || 'all',
          templateId: templateId || 'all',
          alertType,
        });

        // Call BigQuery function
        const getTrailDropOffReportFunction = httpsCallable(functions, 'getTrailDropOffReport');
        const result = await getTrailDropOffReportFunction({
          companyId,
          startMonth,
          endMonth,
          brokerId,
          officeId,
          templateId,
          alertType,
          entityCode,
          anchorWindowToLatestData,
        });

        const data = result.data as {
          totalAlerts: number;
          currentMonthDropOffs: number;
          previousMonthsDropOffs: number;
          significantVariances: number;
          secondYearCommencements: number;
          historicalDropOffs?: number;
          recoveredDropOffs?: number;
          openAlertCount?: number;
          returnedRecordCount?: number;
          recordsTruncated?: boolean;
          dataWindows?: DataWindow[];
          records: Array<{
            loanId: string;
            brokerId: string;
            brokerDisplay: string;
            customerName: string | null;
            lender: string | null;
            settlementDate: string;
            /** Absent while an older backend is still deployed. */
            dischargeDate?: string | null;
            paymentMonth: string;
            trailCents: number;
            priorMonthCents: number | null;
            priorPaymentMonth: string | null;
            monthsSinceSettlement: number;
            alertType: 'current_month_dropoff' | 'previous_months_dropoff' | 'historical_dropoff' | 'recovered_dropoff' | 'significant_variance' | 'second_year_commencement' | null;
            variancePercent: number | null;
            officeId: string | null;
            templateId: string | null;
            entityCode?: string | null;
            recoveredInMonth?: string | null;
            isCurrentDataMonth?: boolean;
            zeroReason?: string | null;
          }>;
          monthlyTrend: Array<{
            month: string;
            currentMonthDropOffs: number;
            previousMonthsDropOffs: number;
            significantVariances: number;
            secondYearCommencements: number;
            historicalDropOffs?: number;
            recoveredDropOffs?: number;
          }>;
          runCompleteness?: Array<{
            templateId: string;
            runStrategy: 'sequential' | 'single';
            availableRuns: number[];
            expectedRunCount: number;
            isComplete: boolean;
          }>;
        };

        console.log(`[useTrailDropOff] BigQuery returned:`, {
          totalAlerts: data.totalAlerts,
          currentMonthDropOffs: data.currentMonthDropOffs,
          previousMonthsDropOffs: data.previousMonthsDropOffs,
          significantVariances: data.significantVariances,
          secondYearCommencements: data.secondYearCommencements,
          records: data.records.length,
        });

        // Store aggregate metrics
        setTotalAlerts(data.totalAlerts);
        setCurrentMonthDropOffs(data.currentMonthDropOffs);
        setPreviousMonthsDropOffs(data.previousMonthsDropOffs);
        setSignificantVariances(data.significantVariances);
        setSecondYearCommencements(data.secondYearCommencements);
        setHistoricalDropOffs(data.historicalDropOffs ?? 0);
        setRecoveredDropOffs(data.recoveredDropOffs ?? 0);
        // Fall back to the legacy sum when an older backend is still deployed, so the
        // headline never reads 0 mid-rollout.
        setOpenAlertCount(
          data.openAlertCount ??
            (data.currentMonthDropOffs + data.previousMonthsDropOffs + data.significantVariances)
        );
        setReturnedRecordCount(data.returnedRecordCount ?? data.records.length);
        setRecordsTruncated(data.recordsTruncated ?? false);
        setDataWindows(data.dataWindows ?? []);

        // Transform BigQuery records to frontend format
        // NOTE: Keep values in CENTS - formatCurrency() expects cents and divides by 100
        const transformedRecords: TrailDropOffRecord[] = data.records.map((record) => ({
          loanId: record.loanId,
          brokerId: record.brokerId,
          brokerDisplay: record.brokerDisplay,
          customerName: record.customerName,
          lender: record.lender,
          settlementDate: new Date(record.settlementDate),
          // Passed straight through as a string. Do NOT wrap in `new Date(...)`.
          dischargeDate: record.dischargeDate ?? null,
          paymentMonth: record.paymentMonth,
          trailCents: record.trailCents,
          priorMonthCents: record.priorMonthCents,
          priorPaymentMonth: record.priorPaymentMonth,
          monthsSinceSettlement: record.monthsSinceSettlement,
          alertType: record.alertType,
          variancePercent: record.variancePercent,
          officeId: record.officeId,
          templateId: record.templateId,
        }));

        setRecords(transformedRecords);
        setMonthlyTrend(data.monthlyTrend);
        setRunCompleteness(data.runCompleteness || []);
      } catch (err: any) {
        console.error('[useTrailDropOff] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch trail drop-off data');
        setRecords([]);
        setMonthlyTrend([]);
        setTotalAlerts(0);
        setCurrentMonthDropOffs(0);
        setPreviousMonthsDropOffs(0);
        setSignificantVariances(0);
        setSecondYearCommencements(0);
        setRunCompleteness([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrailDropOff();
  }, [companyId, startMonth, endMonth, brokerId, officeId, templateId, alertType, entityCode, anchorWindowToLatestData]);

  return {
    records,
    monthlyTrend,
    loading,
    error,
    totalAlerts,
    currentMonthDropOffs,
    previousMonthsDropOffs,
    significantVariances,
    secondYearCommencements,
    historicalDropOffs,
    recoveredDropOffs,
    openAlertCount,
    returnedRecordCount,
    recordsTruncated,
    dataWindows,
    runCompleteness,
  };
}
